import type { ProcessRowGroupForExportParams } from "ag-grid-enterprise";
import type { RefObject } from "react";
import type { GridApi } from "~/components/grid/AgGrid";
import type { GridRequestInput } from "~/gql/graphqlBase.generated";
import type { ExportCellProcessor, ExportFormat } from "~/helpers/grid/ssrmExport";
import type { SsrmExportFetchContext, SsrmExportFetchResult } from "~/helpers/grid/ssrmExportSource";

import { useConfirm } from "material-ui-confirm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchAllRows } from "~/helpers/grid/createSsrmDatasource";
import ssrmExport, { buildExportFileName, buildFetchAllRequest, readGridSortModel } from "~/helpers/grid/ssrmExport";
import logger from "~/helpers/logger";
import snackMessages from "~/helpers/snackMessages";
import { useAppDispatch } from "~/hooks/reduxHooks";
import { useSnacks } from "~/providers/SnackProvider";

const LARGE_EXPORT_ROW_LIMIT = 50_000;
const POST_PROCESS_BATCH_SIZE = 200;
const EXCEL_SHEET_NAME_MAX_LENGTH = 31;
const EXPORT_ACTION = "exporting the grid";

export interface GridFileExportServerSide<TRow extends object> {
	fetchAll?: (context: SsrmExportFetchContext) => Promise<SsrmExportFetchResult<TRow>>;
	getSearchText?: () => string | undefined;
	isReady?: () => boolean;
	gridId?: string;
	postProcessRows?: (rows: TRow[]) => TRow[] | Promise<TRow[]>;
	transformRequest?: (request: GridRequestInput) => GridRequestInput;
}

interface UseGridFileExportArgs<TRow extends object> {
	alwaysIncludeColIds?: readonly string[];
	fileNamePrefix: string;
	gridRef: RefObject<{ api: GridApi } | null>;
	processCell?: ExportCellProcessor;
	processRowGroup?: (params: ProcessRowGroupForExportParams) => string;
	serverSide?: GridFileExportServerSide<TRow>;
	sheetName: string;
	writeRows?: (rows: TRow[], fileName: string, format: ExportFormat) => void;
}

export interface UseGridFileExportReturn {
	cancel: () => void;
	closeFormatMenu: () => void;
	current: number;
	exportAs: (format: ExportFormat) => Promise<void>;
	format: ExportFormat;
	formatMenuAnchor: HTMLElement | null;
	isExporting: boolean;
	openFormatMenu: (anchor: HTMLElement) => void;
	total: number;
}

const postProcessInBatches = async <TRow>(
	rows: TRow[],
	postProcessRows?: (batch: TRow[]) => TRow[] | Promise<TRow[]>
): Promise<TRow[]> => {
	if (!postProcessRows) return rows;
	const processed: TRow[] = [];
	for (let start = 0; start < rows.length; start += POST_PROCESS_BATCH_SIZE) {
		processed.push(...(await postProcessRows(rows.slice(start, start + POST_PROCESS_BATCH_SIZE))));
	}
	return processed;
};

const exportClientSide = (
	api: GridApi,
	format: ExportFormat,
	fileName: string,
	sheetName: string,
	processRowGroupCallback?: (params: ProcessRowGroupForExportParams) => string
): void => {
	if (format === "csv") api.exportDataAsCsv({ fileName, processRowGroupCallback });
	else api.exportDataAsExcel({ fileName, processRowGroupCallback, sheetName });
};

const useGridFileExport = <TRow extends object>({
	alwaysIncludeColIds,
	fileNamePrefix,
	gridRef,
	processCell,
	processRowGroup,
	serverSide,
	sheetName,
	writeRows
}: UseGridFileExportArgs<TRow>): UseGridFileExportReturn => {
	const [isExporting, setIsExporting] = useState(false);
	const [format, setFormat] = useState<ExportFormat>("csv");
	const [current, setCurrent] = useState(0);
	const [total, setTotal] = useState(0);
	const [formatMenuAnchor, setFormatMenuAnchor] = useState<HTMLElement | null>(null);

	const abortRef = useRef(false);
	const isRunningRef = useRef(false);
	const serverSideRef = useRef(serverSide);
	serverSideRef.current = serverSide;

	const dispatch = useAppDispatch();
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();
	const confirm = useConfirm();

	useEffect(
		() => () => {
			abortRef.current = true;
		},
		[]
	);

	const cancel = useCallback(() => {
		abortRef.current = true;
	}, []);

	const openFormatMenu = useCallback((anchor: HTMLElement) => setFormatMenuAnchor(anchor), []);

	const closeFormatMenu = useCallback(() => setFormatMenuAnchor(null), []);

	const exportAs = useCallback(
		async (nextFormat: ExportFormat) => {
			setFormatMenuAnchor(null);
			const api = gridRef.current?.api;
			const serverSideOptions = serverSideRef.current;
			if (!api || isRunningRef.current || serverSideOptions?.isReady?.() === false) return;
			const fileName = buildExportFileName(fileNamePrefix, nextFormat);
			const safeSheetName = sheetName.slice(0, EXCEL_SHEET_NAME_MAX_LENGTH);

			if (!serverSideOptions) {
				exportClientSide(api, nextFormat, fileName, safeSheetName, processRowGroup);
				return;
			}

			const { fetchAll, gridId } = serverSideOptions;
			const onProgress = (next: number, totalCount: number) => {
				setCurrent(next);
				setTotal(totalCount);
			};
			const fetchRows = (rowLimit?: number): Promise<SsrmExportFetchResult<TRow>> => {
				if (fetchAll) {
					return fetchAll({
						abortRef,
						filterModel: api.getFilterModel(),
						onProgress,
						sortModel: readGridSortModel(api),
						stopIfTotalExceeds: rowLimit
					});
				}
				if (!gridId) throw new Error("Server-side export needs a grid id or a fetchAll");
				return fetchAllRows<TRow>(gridId, buildFetchAllRequest(api), {
					abortRef,
					dispatch,
					getSearchText: serverSideOptions.getSearchText,
					onProgress,
					stopIfTotalExceeds: rowLimit,
					transformRequest: serverSideOptions.transformRequest
				});
			};

			abortRef.current = false;
			isRunningRef.current = true;
			setFormat(nextFormat);
			setCurrent(0);
			setTotal(0);
			setIsExporting(true);

			try {
				let result = await fetchRows(LARGE_EXPORT_ROW_LIMIT);
				if (result.overLimit) {
					setIsExporting(false);
					const { confirmed } = await confirm({
						cancellationButtonProps: { variant: "outlined" },
						cancellationText: t("actions.cancel"),
						confirmationButtonProps: { variant: "contained" },
						confirmationText: t("grid.export.largeConfirm"),
						description: t("grid.export.largeDescription"),
						title: t("grid.export.largeTitle", { rows: result.totalCount.toLocaleString() })
					});
					if (!confirmed) return;
					setCurrent(0);
					setIsExporting(true);
					result = await fetchRows();
				}

				const rows = result.cancelled ? [] : await postProcessInBatches(result.rows, serverSideOptions.postProcessRows);
				if (abortRef.current) {
					openSnack(t("grid.export.cancelled"), "info");
					return;
				}
				if (rows.length === 0) {
					openSnack(t("grid.export.noRows"), "info");
					return;
				}
				if (writeRows) writeRows(rows, fileName, nextFormat);
				else ssrmExport(rows, api, nextFormat, fileName, safeSheetName, processCell, alwaysIncludeColIds);
			} catch (error) {
				logger.error(snackMessages.actionError(EXPORT_ACTION), error);
				openSnack(snackMessages.actionError(EXPORT_ACTION), "error");
			} finally {
				isRunningRef.current = false;
				setIsExporting(false);
				setCurrent(0);
				setTotal(0);
			}
		},
		[
			alwaysIncludeColIds,
			confirm,
			fileNamePrefix,
			gridRef,
			openSnack,
			processCell,
			processRowGroup,
			sheetName,
			t,
			writeRows
		]
	);

	return useMemo(
		() => ({
			cancel,
			closeFormatMenu,
			current,
			exportAs,
			format,
			formatMenuAnchor,
			isExporting,
			openFormatMenu,
			total
		}),
		[cancel, closeFormatMenu, current, exportAs, format, formatMenuAnchor, isExporting, openFormatMenu, total]
	);
};

export default useGridFileExport;
